import { FragmentNames, NursingInterventionsRescheduleAliases as NursingReschedule } from '../../../../../codegen'
import BaseController from '../../../../BaseController'
import Controller, { OnCancel, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { CheckboxAPI, FieldAPI, FormAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { FoundationUtils } from '../../../../../utils/foundation-utils';
import { ADEP_E1113Parameters } from '../../../../../utils/parameters';
import { Inject } from '@odinjs/odin';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import TimelineContext, { TimeLineEventMedication } from '../../../services/timeline-service';
import { RequiredFieldParam78 } from '../../../../../constants/utils-adep';
import { Validator } from '../../../../../interfaces/spec-layout';
import { HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../../../constants/endpoints';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ApplicationRoute } from '../../../../../constants/routes';
import moment from 'moment';
import { YesNoType } from '../../../../../utils/enum-utils';
import { Value } from '@philips-internal/oswald-ui-core';
import { DateFilterUtils } from '../../../../../utils/date-filter-utils';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';

const ADEP_1113_MODULE = 'ADEP#1113';
const eliminetedTime = 510990;
const exceededScheduleTime = 1068496;

interface ReasonType {
  reasonDescription: string;
  sequenceNumber: number;
}

interface RescheduleResponse {
  eliminatedList: string;
  inconsistentBatchDescription: string;
  message: number;
}

const ZERO_NUMBER = 0;

@Controller({ fragment: FragmentNames.NURSING_INTERVENTIONS_RESCHEDULE })
export default class GlycemicRescheduleController extends BaseController {

  @Inject(NursingReschedule.CHECKBOX_RESCHEDULE)
  private checkboxApi!: CheckboxAPI;

  @Inject(NursingReschedule.FIELD_JUSTIFICATION)
  private justificationFieldApi!: FieldAPI;

  @Inject(NursingReschedule.FIELD_REASON)
  private reasonFieldApi!: FieldAPI;

  @Inject(NursingReschedule.FORM_SCHEDULE)
  private formApi!: FormAPI;

  @OnMount(NursingReschedule.FORM_SCHEDULE)
  onMountForm(): void {
    this.mountInformationBar();
    this.mountContextAndFields();
    this.checkFunctionParams();
  }

  @OnSubmit(NursingReschedule.FORM_SCHEDULE)
  onSubmitForm(): void {
    this.rescheduleTime();
  }

  @OnCancel(NursingReschedule.FORM_SCHEDULE)
  onCancelForm(): void {
    this.historyService.back();
    applicationContext.popNavigationItem();
  }

  private mountInformationBar(): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    this.context.set({
      encounterId,
      patientId,
    });
  }

  private mountContextAndFields(): void {
    this.context.set({
      establishmentCode: foundationContext.getEstablishment()?.id,
      status: TimelineContext.get().status,
    });

    this.formApi.setData({
      desiredtime: DateFilterUtils.getCurrentDateTimeUtc(),
    });
  }

  private checkFunctionParams(): void {
    this.checkParamsCheckbox();
    this.checkParamsReason();
  }

  private checkParamsCheckbox(): void {
    const param100 = FoundationUtils.findModuleParam(ADEP_1113_MODULE, ADEP_E1113Parameters.WHILE_RESCHEDULE_SHOW_RESCHEDULE_FOLLOWING_HOURS_CHECKBOX_BY_DEFAULT);
    this.checkboxApi.setValue(param100 === YesNoType.YES);
  }

  private checkParamsReason(): void {
    const param78 = FoundationUtils.findModuleParam(ADEP_1113_MODULE, ADEP_E1113Parameters.FIELD_TO_BE_REQUIRED_FOR_RESCHEDULE) as RequiredFieldParam78;
    this.addRequiredField(param78);
  }

  private addRequiredField(requiredField: RequiredFieldParam78): void {
    const validator: Validator = { name: 'required', options: {} };
    switch (requiredField) {
      case RequiredFieldParam78.REASON:
        this.reasonFieldApi.addValidator(validator);
        break;
      case RequiredFieldParam78.JUSTIFICATION:
        this.justificationFieldApi.addValidator(validator);
        break;
      case RequiredFieldParam78.BOTH:
        this.justificationFieldApi.addValidator(validator);
        this.reasonFieldApi.addValidator(validator);
        break;
    }
  }

  private rescheduleTime(): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    const body: Record<string, Value> = {
      ...this.buildRecheduleBody(),
      statusApplication: TimelineContext.get().administrationRouteType
    }

    HttpService.post<RescheduleResponse>(Endpoint.EMAR_SCHEDULE_TIME_NURSING_INTERVENTIONS, undefined, { body })
      .then(response => {
        if (!response.body) {
          return;
        }

        const { message, eliminatedList, inconsistentBatchDescription } = response.body
        applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
          encounterId,
          patientId
        });

        applicationContext.clearNavigationStack();

        if (message === eliminetedTime) {
          if (eliminatedList != null && inconsistentBatchDescription != null) {
            this.showToastTimeItemPrescription(`${inconsistentBatchDescription} ${eliminatedList}`);
          } else if (eliminatedList != null) {
            this.showToastTimeItemPrescription(eliminatedList)
          } else if (inconsistentBatchDescription != null) {
            this.showToastTimeItemPrescription(inconsistentBatchDescription)
          }
          return;
        }

        if(message === exceededScheduleTime) {
          this.showToast('text.not-possible-reschedule-item', ToastLevel.WARNING);
          return;
        }

        this.showToastSuccess();
      }).catch(response => {
        response.body?.errors.forEach((message: string) => this.showToastError(message))
      })
  }

  private buildRecheduleBody(): Record<string, Value> {
    const schedulePrevious = this.checkboxApi.getValue() ? YesNoType.YES : YesNoType.NO;
    const timelineEvent = TimelineContext.get();
    const timeLineEventMedication = timelineEvent as TimeLineEventMedication;
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const formData = this.formApi.getRecord();
    const reasonField = formData.reason as ReasonType;
    const prescriptionNumber = String(timeLineEventMedication?.prescriptionNumber ?? '');
    return {
      currentTime: timelineEvent.start,
      desiredTime: moment(formData.desiredtime as string).utcOffset(0, true).format(),
      encounterNumber: Number(encounterId),
      establishmentCode: foundationContext.getEstablishment()?.id as string,
      internalProcedureNumber: ZERO_NUMBER,
      intervalCode: MedicineAndSolutionsParams.params.cdIntervalId,
      itemCode: timeLineEventMedication?.itemCode,
      itemType: TimelineContext.get().itemType,
      justificationDescription: this.justificationFieldApi.getValue() as string,
      laboratoryStatus: YesNoType.NO,
      prescriptionId: prescriptionNumber.split(' ')[0],
      prescriptionsIds: prescriptionNumber,
      reasonNumber: reasonField?.sequenceNumber || 0,
      scheduleTime: schedulePrevious,
      statusAcm: timeLineEventMedication.pdoPrn ? YesNoType.YES : YesNoType.NO,
      userName: foundationContext.getUser()?.username || '',
    };
  }

  private showToastError(message?: string): void {
    this.showToast(message || 'tws.schedule-medication-infusion-error', ToastLevel.ERROR);
  }

  private showToastTimeItemPrescription(value: string): void {
    const message = MessageService.translate('tws.schedule-medication-infusions-time').replace('#@eliminados#@', value);
    this.showToast(message, ToastLevel.WARNING);
  }

  private showToastSuccess(): void {
    this.showToast('tws.schedule-medication-infusions-success', ToastLevel.SUCCESS);
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'schedule-error-validation',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}
